import styled, { css, keyframes } from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';

/**
 * AI Home — mirrors routes/ai.tsx (shell + thread sidebar) and the chat half of
 * routes/ai.$threadId.tsx in the Remix reference.
 *
 * Radii follow the reference's overridden scale (--radius: 0.875rem):
 *   sm 10 · md 12 · lg 14 · xl 18 · 2xl 22
 */

const FG = 'var(--hd-foreground)';
const MUTED_FG = 'var(--hd-muted-foreground)';
const MUTED = 'var(--hd-muted)';
const BORDER = 'var(--hd-border)';
const BG = 'var(--hd-background)';

const MUTED_60 = 'color-mix(in srgb, var(--hd-muted) 60%, transparent)';
const FG_90 = 'color-mix(in srgb, var(--hd-foreground) 90%, transparent)';
const FG_15 = 'color-mix(in srgb, var(--hd-foreground) 15%, transparent)';
const BG_80 = 'color-mix(in srgb, var(--hd-background) 80%, transparent)';
const BG_60 = 'color-mix(in srgb, var(--hd-background) 60%, transparent)';
const CARD_95 = 'color-mix(in srgb, var(--hd-card) 95%, transparent)';
const MUTED_50 = 'color-mix(in srgb, var(--hd-muted) 50%, transparent)';
const FG_20 = 'color-mix(in srgb, var(--hd-foreground) 20%, transparent)';
const FG_70 = 'color-mix(in srgb, var(--hd-foreground) 70%, transparent)';
/* Tailwind v4's shadow-sm. The scale was renumbered in v4 — what v3 called
   shadow-sm is now shadow-xs, so mapping shadow-sm to `0 1px 2px/0.05` gives
   roughly half the intended weight and the hover barely registers. */
const SHADOW_SM = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)';
const COMPOSER_SHADOW = '0 8px 32px -12px rgba(0, 0, 0, 0.12)';
/* Tailwind's default transition timing, so ports match its feel exactly. */
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

/* Text shimmer for the "Thinking…" / "Responding…" hint. The reference animates
   background-position with motion/react; the same effect as a keyframe avoids
   pulling an animation library in for one label. */
const shimmerSweep = keyframes`
  from { background-position: 100% center; }
  to { background-position: 0% center; }
`;

/* Tailwind's animate-pulse and animate-spin, reproduced for the voice console. */
const pulse = keyframes`
  50% { opacity: 0.5; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/* The reference's animate-fade-up, used by the campaign suggestion chips. */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const StyledShimmer = styled.span`
  position: relative;
  display: inline-block;
  background-image:
    linear-gradient(
        90deg,
        transparent calc(50% - var(--shimmer-spread, 18px)),
        var(--hd-background),
        transparent calc(50% + var(--shimmer-spread, 18px))
      ),
    linear-gradient(var(--hd-muted-foreground), var(--hd-muted-foreground));
  background-repeat: no-repeat, padding-box;
  background-size:
    250% 100%,
    auto;
  background-clip: text;
  color: transparent;
  animation: ${shimmerSweep} 2s linear infinite;
`;

/**
 * Cancels the 15px of horizontal padding StyledLayout puts on every page below
 * 992px. The reference's AI page runs edge to edge under the TopBar, and the
 * campaign split has to reach both edges to read as one surface.
 */
const fullBleed = css`
  width: auto;
  margin: 0 -15px;

  @media (min-width: 992px) {
    margin: 0;
  }
`;

export const StyledAiHome = styled.div`
  /* Brand palette for pages on the new design (see theme/brandTokens.js). */
  ${brandTheme}
  position: relative;
  display: flex;
  /* The reference uses 100dvh - 64px for the TopBar. Here the same figure lives
     in --chrome-offset, which also covers the trial banner when it is showing. */
  height: calc(100dvh - var(--chrome-offset, 64px));
  overflow: hidden;
  background: ${BG};
  color: ${FG};
  ${fullBleed}

  /* A vertical tab riding the left edge, centred on the viewport, that slides
     out with the sidebar. Replaces the small square button; the label reads
     bottom-to-top so the tab stays narrow. */
  .sidebar-toggle {
    position: fixed;
    top: 50%;
    z-index: 30;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 14px 8px;
    border: 1px solid ${BORDER};
    border-left: 0;
    border-radius: 0 22px 22px 0;
    color: ${MUTED_FG};
    background: ${CARD_95};
    box-shadow: 6px 0 20px -10px rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(8px);
    cursor: pointer;
    transform: translateY(-50%);
    transition: all 0.3s ease-out;

    &:hover {
      padding-right: 14px;
      color: ${FG};
      box-shadow: 6px 0 24px -8px rgba(0, 0, 0, 0.28);
    }

    &:hover .toggle-icon {
      transform: scale(1.05);
    }

    ${({ $sidebarOpen }) => css`
      left: ${$sidebarOpen ? '256px' : '0'};
    `}

    .toggle-stack {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    }

    .toggle-icon {
      transition: transform 0.3s ease-out;
    }

    .toggle-label {
      font-size: 9px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.18em;
      writing-mode: vertical-rl;
    }
  }

  .ai-sidebar {
    flex-shrink: 0;
    border-right: 1px solid ${BORDER};
    background: ${BG};
    transition:
      width 0.3s ease-in-out,
      opacity 0.3s ease-in-out;

    ${({ $sidebarOpen }) =>
      $sidebarOpen
        ? css`
            width: 256px;
            opacity: 1;
          `
        : css`
            width: 0;
            overflow: hidden;
            opacity: 0;
          `}
  }

  .ai-sidebar-inner {
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: 100%;
    padding: 16px;
  }

  .new-chat-btn {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    height: 40px;
    padding: 0 12px;
    border: none;
    border-radius: 14px;
    font-size: 14px;
    font-weight: 500;
    color: #fff;
    background: var(--hd-gradient-houdiny);
    box-shadow: var(--hd-shadow-card);
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 0.9;
    }

    span {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .thread-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--hd-scrollbar-thumb) transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--hd-scrollbar-thumb);
      border-radius: 999px;
    }
  }

  .thread-list-label {
    padding: 6px 8px;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${MUTED_FG};
  }

  .thread-list-empty {
    padding: 12px 8px;
    font-size: 12px;
    color: ${MUTED_FG};
  }

  .thread-row {
    position: relative;
    display: flex;
    align-items: center;
    border-radius: 12px;

    &.active {
      background: ${MUTED};
    }

    &:hover .thread-delete {
      opacity: 1;
    }
  }

  .thread-open {
    display: flex;
    flex: 1;
    align-items: center;
    gap: 8px;
    min-width: 0;
    padding: 8px;
    border: none;
    border-radius: 12px;
    font-size: 14px;
    text-align: left;
    color: ${FG_90};
    background: transparent;
    cursor: pointer;

    &:hover {
      background: ${MUTED_60};
    }

    svg {
      flex-shrink: 0;
      color: ${MUTED_FG};
    }

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .thread-delete {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 24px;
    height: 24px;
    margin-right: 4px;
    border: none;
    border-radius: 4px;
    color: ${MUTED_FG};
    background: transparent;
    opacity: 0;
    cursor: pointer;
    transition:
      opacity 0.15s ease,
      color 0.15s ease;

    &:hover {
      color: var(--hd-destructive);
      background: ${BG};
    }
  }

  /* The Chats / Close tab rides over the left edge of the chat column, so the
     text keeps a 16px gutter beyond the usual px-4 / md:px-8 to clear it.
     Without it, replies ran underneath the tab. */
  --rail-width: 16px;

  /* Chat + whiteboard after the thread sidebar. Version 2 splits 44% / remainder
     inside the main row (not including the sidebar). Wrapping the two panes the
     same way means opening the 256px sidebar shrinks both columns together. */
  .ai-workspace {
    display: flex;
    flex: 1;
    min-width: 0;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }

  .ai-main {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
    transition: width 0.5s ease-out;

    /* Version 2: 44% of the workspace when a campaign is open, otherwise full width. */
    &.campaign-mode {
      flex: none;
      width: 44%;
    }
  }

  .campaign-pane {
    display: flex;
    flex: 1;
    min-width: 0;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }

  /* v2 washes the whole chat column rather than only the top of the empty
     state: background → primary/2.5% → primary/6%, top to bottom. */
  .ai-glow {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background: linear-gradient(
      to bottom,
      ${BG},
      color-mix(in srgb, var(--hd-primary) 2.5%, transparent),
      color-mix(in srgb, var(--hd-primary) 6%, transparent)
    );
  }

  .ai-topline {
    position: relative;
    z-index: 10;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    min-height: 44px;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 500;
    color: ${MUTED_FG};
  }

  .ai-topline-left {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ai-topline-right {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 12px;
  }

  .campaign-exit {
    padding: 0;
    border: none;
    font-size: 12px;
    color: ${MUTED_FG};
    background: transparent;
    cursor: pointer;

    &:hover {
      color: ${FG};
    }
  }

  .ai-conversation {
    position: relative;
    z-index: 10;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0;
    scrollbar-width: thin;
    scrollbar-color: var(--hd-scrollbar-thumb) transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--hd-scrollbar-thumb);
      border-radius: 999px;
    }

    /* Centre the greeting in the space instead of pinning it to the top — the
       reference's pt-10/pt-16 assumes a taller viewport, and here it left a
       dead band above the composer. An auto margin on the child rather than
       justify-content does the centring, so a short viewport can still scroll
       to the top of the block instead of clipping it. */
    &.is-empty {
      display: flex;
      /* No scrollbar on the greeting screen: the second page of suggestions is
         taller than the first and would otherwise flash one in. */
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }

      padding: 0 16px 16px calc(16px + var(--rail-width));

      @media (min-width: 768px) {
        padding: 0 32px 16px calc(32px + var(--rail-width));
      }
    }
  }

  .ai-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    /* max-w-3xl with pt-10, widening to pt-16 at md. No side padding of its own:
       the column around it supplies px-4 / px-8, exactly as the reference does. */
    max-width: 768px;
    margin: auto;
    padding: 40px 0 0;

    @media (min-width: 768px) {
      padding-top: 64px;
    }

    > img {
      height: 48px;
      width: auto;
      opacity: 0.9;
    }

    /* Laptop-height screens: version3's spacing assumes a tall viewport, and at
       ~900px the second row of suggestions ended up behind the composer. Tighten
       the vertical rhythm so the whole block fits above it. */
    @media (max-height: 900px) {
      padding-top: 16px;

      > img {
        height: 40px;
      }

      .ai-greeting {
        margin-top: 16px;
      }

      .suggestions {
        margin-top: 20px;
      }
    }

    /* 1366×768 and similar laptops: even the tightened rhythm left the second row
       of suggestions behind the composer, so the greeting shrinks a step further. */
    @media (max-height: 800px) {
      padding-top: 4px;

      > img {
        height: 32px;
      }

      .ai-greeting {
        margin-top: 8px;
        font-size: 30px;
      }

      .ai-subtitle {
        margin-top: 6px;
        font-size: 14px;
      }

      .suggestions {
        margin-top: 14px;
      }

      .suggestions-dots {
        margin-top: 8px;
      }
    }
  }

  .ai-greeting {
    margin: 28px 0 0;
    font-size: 34px;
    font-weight: 600;
    /* The reference's leading-tight. At 1.1 the gradient line below clips the
       descender on "today?" — background-clip: text cuts to the line box. */
    line-height: 1.25;
    letter-spacing: -0.025em;
    text-align: center;
    /* The app's global h1 rule capitalises every word; the greeting is a sentence. */
    text-transform: none;
    color: ${FG};

    @media (min-width: 768px) {
      font-size: 40px;
    }

    .gradient-line {
      display: block;
      background-image: var(--hd-gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      -webkit-text-fill-color: transparent;
    }
  }

  .ai-subtitle {
    max-width: 448px;
    margin: 12px 0 0;
    font-size: 15px;
    text-align: center;
    color: ${MUTED_FG};
  }

  /* ConversationContent is "mx-auto max-w-4xl flex flex-col gap-8" — the column
     is centred and capped; each message then caps itself at 95% of it. */
  .message-stack {
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
    max-width: 896px;
    margin: 0 auto;
    padding: 16px 16px 16px calc(16px + var(--rail-width));

    @media (min-width: 768px) {
      padding: 16px 32px 16px calc(32px + var(--rail-width));
    }
  }

  /* Message: max-w-[95%], user pushed right with a secondary-filled bubble,
     assistant rendered as bare foreground text. */
  .message {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    max-width: 95%;
    font-size: 14px;
    line-height: 20px;

    &:focus-within {
      position: relative;
      z-index: 5;
    }

    &.from-user {
      margin-left: auto;
      align-items: flex-end;
    }

    .bubble {
      width: fit-content;
      max-width: 100%;
      min-width: 0;
      overflow-wrap: anywhere;
      padding: 12px 16px;
      border-radius: 14px;
      color: ${FG};
      background: var(--hd-secondary);
      white-space: pre-wrap;
    }

    /* Held or abandoned turns read as secondary text until they settle. */
    &.is-unsettled .plain {
      color: ${MUTED_FG};
    }

    .message-status {
      color: ${MUTED_FG};
      font-size: 12px;
      margin-bottom: 4px;
    }

    /* The approval card for a plan that will send.
       Nothing has run at this point: Master AI is holding the plan and executes
       it only when the Launch button posts the confirmation id back. It reads as
       a summary the user signs off, not as a warning banner. */
    .launch-confirm {
      margin-top: 14px;
      padding: 16px;
      border-radius: 18px;
      background: var(--hd-card, #fff);
      box-shadow:
        inset 0 0 0 1px ${BORDER},
        0 8px 24px -18px rgb(0 0 0 / 45%);
      animation: ${fadeUp} 0.32s ease-out both;
    }

    .launch-confirm-head {
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }

    .launch-confirm-icon {
      display: grid;
      flex-shrink: 0;
      place-items: center;
      width: 34px;
      height: 34px;
      border-radius: 12px;
      color: #fff;
      background: var(--hd-gradient-houdiny);
    }

    .launch-confirm-title {
      font-size: 14px;
      font-weight: 600;
      color: ${FG};
    }

    .launch-confirm-sub {
      margin: 3px 0 0;
      font-size: 12.5px;
      line-height: 1.5;
      color: ${MUTED_FG};
    }

    .launch-confirm-facts {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 14px;
    }

    /* The exact text an inbox reply will send. */
    .launch-confirm-quote {
      margin: 14px 0 0;
      padding: 10px 12px;
      border-left: 3px solid ${BORDER};
      border-radius: 6px;
      background: ${MUTED};
      color: ${FG};
      font-size: 13px;
      line-height: 1.55;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      max-height: 180px;
      overflow-y: auto;
    }

    .launch-confirm-subject {
      margin: 14px 0 0;
      font-size: 13px;
      color: ${FG};
      overflow-wrap: anywhere;

      span {
        margin-right: 8px;
        font-weight: 600;
      }
    }

    .launch-confirm-subject + .launch-confirm-quote {
      margin-top: 8px;
    }

    .launch-confirm-fact {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 10px;
      border-radius: 999px;
      font-size: 11.5px;
      font-weight: 500;
      color: ${FG};
      background: var(--hd-muted);

      svg {
        color: ${MUTED_FG};
      }
    }

    .launch-confirm-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 16px;
    }

    .launch-confirm-cancel,
    .launch-confirm-go {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      height: 38px;
      padding: 0 18px;
      border: 0;
      border-radius: 999px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition:
        background 0.15s ease,
        color 0.15s ease,
        opacity 0.15s ease;

      &:disabled {
        opacity: 0.65;
        cursor: default;
      }
    }

    .launch-confirm-cancel {
      color: ${MUTED_FG};
      background: var(--hd-secondary);

      &:hover:not(:disabled) {
        color: ${FG};
      }
    }

    .launch-confirm-go {
      color: #fff;
      background: var(--hd-gradient-houdiny);
      box-shadow: 0 8px 20px -10px color-mix(in srgb, var(--hd-primary) 70%, transparent);

      &:hover:not(:disabled) {
        filter: brightness(1.06);
      }

      .spin {
        animation: ${spin} 1s linear infinite;
      }
    }

    .plain {
      color: ${FG};
      white-space: pre-wrap;

      /* AssistantText emits real elements, so paragraphs carry their own
         spacing rather than relying on pre-wrap newlines.

         The inherited colour is load-bearing: GlobalStyles sets a bare
         p { color: var(--base-text-color) } at #9d9d9d, which beats the colour
         set on this container and washed every assistant reply out to light
         grey. The reference renders them at plain text-foreground. */
      .assistant-paragraph {
        margin: 0;
        color: inherit;
      }

      .assistant-paragraph + .assistant-paragraph {
        margin-top: 8px;
      }

      .message-actions {
        display: flex;
        align-items: center;
        gap: 2px;
        margin-top: 8px;

        > button,
        .message-more > button {
          display: grid;
          place-items: center;
          width: 28px;
          height: 28px;
          padding: 0;
          border: none;
          border-radius: 8px;
          color: ${MUTED_FG};
          background: transparent;
          cursor: pointer;

          &:hover,
          &[aria-expanded='true'] {
            color: ${FG};
            background: ${MUTED};
          }

          &.is-active {
            color: ${FG};
          }
        }
      }

      .message-more {
        position: relative;
      }

      .message-usage {
        position: absolute;
        top: calc(100% + 6px);
        left: 0;
        z-index: 30;
        min-width: 168px;
        padding: 10px 12px;
        border: 1px solid ${BORDER};
        border-radius: 12px;
        background: var(--hd-card);
        box-shadow: var(--hd-shadow-elevated);
        white-space: nowrap;

        /* No room under the button: open above it instead. */
        &.is-up {
          top: auto;
          bottom: calc(100% + 6px);
        }

        /* Label on the left, its value on the right. */
        p {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 16px;
          margin: 0;
          font-size: 13px;
          line-height: 18px;
          font-weight: 500;
          color: ${MUTED_FG};
        }

        strong {
          font-weight: 600;
          font-variant-numeric: tabular-nums;
          color: ${FG};
        }

        p + p {
          margin-top: 2px;
        }
      }

      .assistant-list {
        margin: 8px 0;
        padding-left: 20px;
        white-space: normal;
        color: inherit;

        li {
          color: inherit;
        }

        li + li {
          margin-top: 4px;
        }
      }

      /* What a lead wrote, or a draft about to go out, set apart from the agent's own words. */
      .assistant-quote {
        margin: 6px 0;
        padding: 8px 12px;
        border-left: 3px solid ${BORDER};
        border-radius: 6px;
        background: ${MUTED};
        white-space: normal;
        color: inherit;

        p {
          margin: 0;
          color: inherit;
          font-style: italic;
        }

        p + p {
          margin-top: 4px;
        }
      }

      /* A link shown short, with the whole thing on the clipboard. */
  .assistant-url {
    display: inline-flex;
    align-items: stretch;
    max-width: 100%;
    margin: 0 2px;
    vertical-align: middle;
    border: 1px solid ${BORDER};
    border-radius: 8px;
    overflow: hidden;
    background: ${BG};

    a {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      min-width: 0;
      padding: 3px 8px;
      font-size: 12px;
      text-decoration: none;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: var(--hd-primary);

      &:hover {
        background: color-mix(in srgb, var(--hd-primary) 8%, transparent);
      }

      svg {
        flex-shrink: 0;
      }
    }

    button {
      display: grid;
      place-items: center;
      flex-shrink: 0;
      width: 26px;
      border: 0;
      border-left: 1px solid ${BORDER};
      color: ${MUTED_FG};
      background: transparent;
      cursor: pointer;

      &:hover {
        color: var(--hd-primary);
        background: color-mix(in srgb, var(--hd-primary) 8%, transparent);
      }
    }
  }
  .assistant-link {
        color: var(--hd-primary);
        text-decoration: none;
        border-bottom: 1px solid color-mix(in srgb, var(--hd-primary) 35%, transparent);
        transition: border-color 0.15s ease;

        &:hover {
          border-bottom-color: var(--hd-primary);
        }
      }
    }
  }

  .ai-error {
    max-width: 576px;
    margin: 16px auto 0;
    padding: 8px 12px;
    border: 1px solid color-mix(in srgb, var(--hd-destructive) 30%, transparent);
    border-radius: 12px;
    font-size: 12px;
    color: var(--hd-destructive);
    background: color-mix(in srgb, var(--hd-destructive) 5%, transparent);
  }

  .ai-composer-wrap {
    position: relative;
    z-index: 10;
    flex-shrink: 0;
    padding: 8px 16px 32px calc(16px + var(--rail-width));

    @media (min-width: 768px) {
      padding: 8px 32px 32px calc(32px + var(--rail-width));
    }

    /* Short laptop screens: the space under the composer goes to the chat instead. */
    @media (max-height: 800px) {
      padding-bottom: 16px;
    }
  }

  .ai-composer-inner {
    width: 100%;
    max-width: 896px;
    margin: 0 auto;

    /* max-w-full inside the campaign flow, where the column is already narrow. */
    &.is-wide {
      max-width: 100%;
    }
  }

  /* Answers to whatever the Campaign Designer just asked, offered as chips over
     the composer. Typing the same thing works identically — these are shortcuts,
     not the only way through the flow. */
  .composer-chips {
    margin-bottom: 12px;
  }

  /* Offered beside the composer when what is on screen can be filled in rather
     than described — a schedule today. The hint beside it says typing still
     works, so the button reads as a shortcut and not as the only way in. */
  .composer-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 12px;
  }

  .composer-action {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 7px 13px;
    border: 1px solid color-mix(in srgb, var(--hd-primary) 40%, transparent);
    border-radius: 999px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--hd-primary);
    background: color-mix(in srgb, var(--hd-primary) 10%, transparent);
    cursor: pointer;
    transition:
      background 0.15s ease,
      border-color 0.15s ease;

    &:hover:not(:disabled) {
      background: color-mix(in srgb, var(--hd-primary) 16%, transparent);
      border-color: color-mix(in srgb, var(--hd-primary) 60%, transparent);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .composer-action-hint {
    font-size: 11.5px;
    color: ${MUTED_FG};
  }

  .composer-chips-label {
    margin-bottom: 8px;
    font-size: 12px;
    color: ${MUTED_FG};
  }

  .composer-chips-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .composer-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border: 1px solid ${BORDER};
    border-radius: 999px;
    font-size: 13px;
    text-align: left;
    color: ${FG_90};
    background: ${BG};
    cursor: pointer;
    animation: ${fadeUp} 0.5s ease-out both;
    transition:
      border-color 0.15s ease,
      background 0.15s ease;

    &:hover {
      border-color: ${FG_20};
      background: ${MUTED_50};
    }

    .chip-text {
      display: -webkit-box;
      overflow: hidden;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;

      /* Lead-count chips show a bare number: tabular-nums font-medium. */
      &.is-number {
        font-weight: 500;
        font-variant-numeric: tabular-nums;
      }
    }

    .chip-meta {
      flex-shrink: 0;
      font-size: 11px;
      font-variant-numeric: tabular-nums;
      color: color-mix(in srgb, var(--hd-muted-foreground) 70%, transparent);
    }

    .chip-go {
      flex-shrink: 0;
      color: color-mix(in srgb, var(--hd-muted-foreground) 50%, transparent);
      transition:
        transform 0.15s ease,
        color 0.15s ease;
    }

    &:hover .chip-go {
      transform: translateX(2px);
      color: ${FG_70};
    }
  }

  /* Focus treatment copied from the shadcn InputGroup the reference uses:
     the border colour never moves, and a 1px ring in --ring is layered outside
     it while the control has focus. Transition covers colour and box-shadow
     only, as the reference's transition-[color,box-shadow] does. */
  .ai-composer {
    margin-top: -5px;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    background: ${BG_80};
    box-shadow: ${COMPOSER_SHADOW};
    backdrop-filter: blur(8px);
    outline: none;
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;

    @supports (backdrop-filter: blur(1px)) {
      background: ${BG_60};
    }

    &:focus-within {
      border-color: var(--hd-ring);
    }

    &:has(.is-over) {
      border-color: color-mix(in srgb, ${FG} 28%, transparent);
    }

    .ai-composer-group {
      overflow: hidden;
      border-radius: inherit;
    }

    textarea {
      display: block;
      width: 100%;
      min-height: 86px;
      max-height: 192px;

      /* Short laptop screens (1366×768): a shorter empty field, so the greeting fits above it. */
      @media (max-height: 800px) {
        min-height: 56px;
      }

      padding: 12px;
      border: none;
      border-radius: 0;
      font-family: inherit;
      font-size: 15px;
      line-height: 1.5;
      color: ${FG};
      background: transparent;
      resize: none;
      outline: none;
      box-shadow: none;
      /* version3 grows the field with its content (field-sizing-content). */
      field-sizing: content;

      /* The reference asks for text-[15px], but the base md:text-sm survives
         the class merge, so from 768px up it renders at 14px / 20px. */
      @media (min-width: 768px) {
        font-size: 14px;
        line-height: 20px;
      }

      &:focus,
      &:focus-visible {
        border: none;
        outline: none;
        box-shadow: none;
      }

      &::placeholder {
        color: ${MUTED_FG};
      }

      &[aria-invalid='true'] {
        border: none;
        outline: none;
        box-shadow: none;
      }
    }
  }

  .ai-composer-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    padding: 6px 12px 12px;
  }

  .ai-composer-hint {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex: 1;
    font-size: 11px;
    font-weight: 500;
    color: ${MUTED_FG};

    svg {
      flex-shrink: 0;
      color: var(--hd-primary);
    }

    &.is-over {
      color: ${FG_70};

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  .ai-char-count {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 500;
    color: ${MUTED_FG};
    white-space: nowrap;
    font-variant-numeric: tabular-nums;

    &.is-over {
      color: ${FG};
    }
  }

  /* Out of AI Home credits: what happened, and the two ways on, as buttons. */
  .ai-credits-notice {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 14px;
    margin-bottom: 10px;
    padding: 14px 16px;
    border: 1px solid color-mix(in srgb, var(--hd-primary) 22%, ${BORDER});
    border-radius: 16px;
    background:
      linear-gradient(
        135deg,
        color-mix(in srgb, var(--hd-primary) 7%, transparent),
        color-mix(in srgb, var(--hd-primary) 2%, transparent)
      ),
      var(--hd-card);
    box-shadow: ${SHADOW_SM};
    animation: ${fadeUp} 0.25s ${EASE};

    .credits-icon {
      display: inline-flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 12px;
      color: var(--hd-primary);
      background: color-mix(in srgb, var(--hd-primary) 12%, transparent);
    }

    .credits-copy {
      flex: 1 1 220px;
      min-width: 0;
    }

    .credits-title {
      font-size: 14px;
      font-weight: 600;
      line-height: 1.35;
      color: ${FG};
    }

    .credits-text {
      margin: 2px 0 0;
      font-size: 13px;
      line-height: 1.45;
      color: ${MUTED_FG};
    }

    .credits-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-left: auto;
    }

    .credits-key,
    .credits-buy {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      height: 34px;
      padding: 0 14px;
      border-radius: 999px;
      font-size: 13px;
      font-weight: 600;
      white-space: nowrap;
      cursor: pointer;
      transition:
        background 0.15s ease,
        border-color 0.15s ease,
        color 0.15s ease,
        filter 0.15s ease;
    }

    .credits-key {
      border: 1px solid ${BORDER};
      color: ${FG};
      background: var(--hd-card);

      &:hover {
        border-color: color-mix(in srgb, var(--hd-primary) 40%, ${BORDER});
      }
    }

    .credits-buy {
      border: 0;
      color: #fff;
      background: var(--hd-gradient-houdiny);
      box-shadow: 0 8px 20px -10px color-mix(in srgb, var(--hd-primary) 70%, transparent);

      &:hover {
        filter: brightness(1.06);
      }
    }

    .credits-key:focus-visible,
    .credits-buy:focus-visible {
      outline: 2px solid color-mix(in srgb, var(--hd-primary) 45%, transparent);
      outline-offset: 2px;
    }

    /* A phone: the buttons go full width under the text, the main one first. */
    @media (max-width: 560px) {
      .credits-actions {
        width: 100%;
        margin-left: 0;
      }

      .credits-key,
      .credits-buy {
        flex: 1 1 140px;
        justify-content: center;
        padding: 0 12px;
      }

      .credits-buy {
        order: -1;
      }
    }
  }

  /* AI provider: the connected model is answering. */
  .ai-provider-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 10px 2px 8px;
    border: 1px solid var(--hd-border);
    border-radius: 999px;
    font-size: 11px;
    line-height: 18px;
    font-weight: 500;
    color: var(--hd-foreground);
    background: var(--hd-background);
  }

  /* The provider failed on a message, so Tigaly AI answered it. */
  .ai-provider-notice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 10px;
    padding: 10px 12px;
    border: 1px solid #fde68a;
    border-radius: 14px;
    font-size: 13px;
    line-height: 18px;
    color: #92400e;
    background: #fffbeb;

    > svg {
      flex-shrink: 0;
      margin-top: 2px;
    }

    span {
      flex: 1;
      min-width: 0;
    }

    .notice-link {
      font-weight: 600;
      color: #92400e;
      text-decoration: underline;
    }

    .notice-close {
      display: grid;
      flex-shrink: 0;
      place-items: center;
      width: 22px;
      height: 22px;
      padding: 0;
      border: none;
      border-radius: 8px;
      color: #92400e;
      background: transparent;
      cursor: pointer;

      &:hover {
        background: color-mix(in srgb, #f59e0b 15%, transparent);
      }
    }
  }

  .ai-send-btn {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 12px;
    color: var(--hd-primary-foreground);
    background: var(--hd-primary);
    box-shadow: ${SHADOW_SM};
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover:not(:disabled) {
      background: color-mix(in oklab, var(--hd-primary) 90%, transparent);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* Matches PromptInputSubmit in the reference: spinner while submitted,
       stop square while streaming. */
    .spin {
      animation: ${spin} 1s linear infinite;
    }
  }

  /* The suggestion pager. v2 moved this out of the composer and into the empty
     state, under the subtitle, and pages between two sets of cards. */
  .suggestions {
    width: 100%;
    margin-top: 32px;
  }

  .suggestions-viewport {
    position: relative;
  }

  .quick-actions {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    width: 100%;

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  /* Sit just outside the grid, vertically centred, as -left-5 / -right-5 do. */
  .suggestions-arrow {
    position: absolute;
    top: 50%;
    z-index: 10;
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid ${BORDER};
    border-radius: 999px;
    color: ${FG};
    background: ${BG};
    box-shadow: var(--hd-shadow-card);
    cursor: pointer;
    transform: translateY(-50%);
    transition: all 0.15s ${EASE};

    &:hover {
      background: ${MUTED};
      box-shadow: var(--hd-shadow-card-hover);
    }

    &.prev {
      left: -20px;
    }

    &.next {
      right: -20px;
    }
  }

  .suggestions-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 12px;

    span {
      height: 6px;
      width: 6px;
      border-radius: 999px;
      background: ${BORDER};
      transition: all 0.15s ${EASE};

      &.active {
        width: 20px;
        background: var(--hd-primary);
      }
    }
  }

  /* ConversationScrollButton: shown when the newest message is out of view. */
  .ai-conversation-wrap {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
  }

  .ai-scroll-bottom {
    position: absolute;
    bottom: 16px;
    left: 50%;
    z-index: 20;
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid var(--hd-input, ${BORDER});
    border-radius: 999px;
    color: ${FG};
    background: ${BG};
    box-shadow: ${SHADOW_SM};
    cursor: pointer;
    transform: translateX(-50%);
    transition: background 0.15s ease;

    &:hover {
      background: var(--hd-accent);
    }
  }
`;

/* Chat / Voice switch. Sits in the chat topline and, in voice mode, pinned to
   the top-right of the console. */
export const StyledModeToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border: 1px solid ${BORDER};
  border-radius: 999px;
  background: var(--hd-card);
  box-shadow: var(--hd-shadow-card);

  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border: none;
    border-radius: 999px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: ${MUTED_FG};
    background: transparent;
    cursor: pointer;
    transition:
      color 0.15s ease,
      background 0.15s ease;

    &:hover {
      color: ${FG};
    }

    &.active {
      color: #fff;
      background: var(--hd-gradient-houdiny);
      box-shadow: var(--hd-shadow-card);
    }
  }
`;

export const StyledVoiceShell = styled.div`
  ${brandTheme}
  position: relative;
  display: flex;
  height: calc(100dvh - var(--chrome-offset, 64px));
  overflow: hidden;
  /* slate-950 — the reference keeps the voice console on a fixed dark ground in
     both themes; JarvisVoice paints over it, this shows at the edges. */
  background: #020617;
  ${fullBleed}

  .voice-pane {
    position: relative;
    width: 100%;
    transition: width 0.5s ease-out;

    ${({ $campaignMode }) =>
      $campaignMode &&
      css`
        width: 46%;
      `}
  }

  .voice-campaign-pane {
    display: flex;
    flex: 1 1 54%;
    min-width: 0;
    min-height: 0;
    height: 100%;
    overflow: hidden;
    background: ${BG};
  }

  .voice-mode-toggle {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 20;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

export const StyledJarvisVoice = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: ${BG};

  .voice-stage {
    position: relative;
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    min-height: 0;
    padding: 0 24px;
  }

  .voice-orb {
    position: absolute;
    top: 50%;
    left: 50%;
    width: min(78vh, 720px);
    height: min(78vh, 720px);
    transform: translate(-50%, -50%);
  }

  .mic-btn {
    position: relative;
    z-index: 10;
    display: grid;
    place-items: center;
    width: 96px;
    height: 96px;
    border: none;
    border-radius: 50%;
    color: #fff;
    background: var(--hd-gradient-houdiny);
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -4px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition:
      transform 0.3s ease,
      box-shadow 0.3s ease,
      opacity 0.3s ease;

    &:hover:not(:disabled) {
      transform: scale(1.04);
    }

    &.listening {
      transform: scale(1.04);
      box-shadow:
        0 20px 25px -5px rgba(0, 0, 0, 0.1),
        0 8px 10px -6px rgba(0, 0, 0, 0.1);
    }

    &.busy {
      opacity: 0.7;
    }

    &:disabled {
      cursor: default;
    }

    .spin {
      animation: ${spin} 1s linear infinite;
    }

    .pulse {
      animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }
  }

  .voice-status {
    position: absolute;
    bottom: 32px;
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 24px;
  }

  .voice-state {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
    color: ${MUTED_FG};

    .state-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--hd-gradient-houdiny);

      &.pulse {
        animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      }
    }
  }

  .voice-subtitle {
    max-width: 576px;
    margin: 8px 0 0;
    font-size: 14px;
    line-height: 1.625;
    text-align: center;
    color: ${MUTED_FG};
  }

  .voice-error {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    padding: 8px 12px;
    border: 1px solid color-mix(in srgb, var(--hd-destructive) 20%, transparent);
    border-radius: 14px;
    font-size: 12px;
    color: var(--hd-destructive);
    background: color-mix(in srgb, var(--hd-destructive) 5%, transparent);
  }

  .voice-transcript {
    position: relative;
    flex-shrink: 0;
    max-height: 30%;
    overflow-y: auto;
    padding: 16px 24px;
    border-top: 1px solid color-mix(in srgb, var(--hd-border) 60%, transparent);
    background: ${BG};
    scrollbar-width: thin;
    scrollbar-color: var(--hd-scrollbar-thumb) transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--hd-scrollbar-thumb);
      border-radius: 999px;
    }
  }

  .transcript-inner {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 672px;
    margin: 0 auto;
  }

  .transcript-row {
    display: flex;
    gap: 12px;
    font-size: 13px;
    line-height: 1.625;
  }

  .transcript-role {
    flex-shrink: 0;
    width: 56px;
    font-size: 12px;
    font-weight: 600;
    color: ${MUTED_FG};

    &.assistant {
      background-image: var(--hd-gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      -webkit-text-fill-color: transparent;
    }
  }

  .transcript-text {
    color: ${MUTED_FG};

    &.assistant {
      color: ${FG};
    }
  }
`;

/* hover:border-foreground/15 + hover:bg-background + hover:shadow-sm, on
   transition-all at Tailwind's 150ms default. A deliberately quiet change —
   the fill only lifts from 60% to full, so the border and shadow carry it. */
export const StyledQuickAction = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid ${BORDER};
  border-radius: 18px;
  text-align: left;
  background: ${BG_60};
  cursor: pointer;
  transition: all 0.15s ${EASE};

  &:hover:not(:disabled) {
    border-color: ${FG_15};
    background: ${BG};
    box-shadow: ${SHADOW_SM};
  }

  &:disabled {
    cursor: not-allowed;
  }

  /* Cards the reference has not wired up yet read as inert rather than broken. */
  &.is-coming-soon {
    opacity: 0.6;
  }

  .qa-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    color: ${MUTED_FG};
    background: ${MUTED};
  }

  .qa-icon {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 14px;

    ${({ $from, $to, $fg, $fgDark }) => css`
      color: ${$fg};
      background: linear-gradient(to bottom right, ${$from}, ${$to});

      [data-theme='dark'] &,
      html.dark & {
        color: ${$fgDark};
      }
    `}
  }

  .qa-text {
    min-width: 0;
  }

  .qa-label {
    display: block;
    overflow: hidden;
    font-size: 13px;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${FG};
  }

  .qa-hint {
    display: block;
    overflow: hidden;
    font-size: 11.5px;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${MUTED_FG};
  }
`;

/* Replaces window.confirm for thread deletion. Deliberately small: one line of
   copy, two buttons, no icon — a native dialog's job done in the page's own
   type and colour. */
const dialogFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const dialogPopIn = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

export const StyledConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: grid;
  place-items: center;
  padding: 20px;
  background: color-mix(in srgb, var(--hd-foreground) 28%, transparent);
  backdrop-filter: blur(3px);
  animation: ${dialogFadeIn} 0.14s ${EASE};
`;

export const StyledConfirmDialog = styled.div`
  width: 100%;
  max-width: 360px;
  padding: 22px;
  border: 1px solid ${BORDER};
  border-radius: 18px;
  background: var(--hd-popover);
  box-shadow: var(--hd-shadow-elevated);
  animation: ${dialogPopIn} 0.16s ${EASE};

  .confirm-title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${FG};
  }

  .confirm-text {
    margin: 6px 0 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${MUTED_FG};
  }

  .confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 20px;
  }

  button {
    height: 34px;
    padding: 0 14px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition:
      background 0.15s ${EASE},
      border-color 0.15s ${EASE},
      opacity 0.15s ${EASE};

    &:focus-visible {
      outline: 2px solid var(--hd-ring);
      outline-offset: 2px;
    }
  }

  .confirm-cancel {
    border: 1px solid ${BORDER};
    color: ${FG_90};
    background: transparent;

    &:hover {
      background: ${MUTED_60};
    }
  }

  .confirm-delete {
    border: 1px solid transparent;
    color: var(--hd-destructive-foreground);
    background: var(--hd-destructive);

    &:hover {
      opacity: 0.9;
    }
  }
`;
